import {
  type LucideIcon,
  Bot,
  Settings,
  ExternalLink,
  MessageSquareText,
} from 'lucide-react'

// Route registry - single source of truth for route segments
export const ROUTE_REGISTRY = {
  ACTIVITY: {
    id: 'ACTIVITY',
    path: '/activity',
    label: 'Activity',
  },
  ACTIVITY_OVERVIEW: {
    id: 'ACTIVITY_OVERVIEW',
    path: '/activity',
    label: 'Overview',
  },
  ACTIVITY_RECENT: {
    id: 'ACTIVITY_RECENT',
    path: '/activity/recent',
    label: 'Recent',
  },
  ACTIVITY_ANALYTICS: {
    id: 'ACTIVITY_ANALYTICS',
    path: '/activity/analytics',
    label: 'Analytics',
  },
  AGENTS: {
    id: 'AGENTS',
    path: '/agents',
    label: 'Agents',
  },
  AGENTS_LIST: {
    id: 'AGENTS_LIST',
    path: '/agents/list',
    label: 'List',
  },
  AGENTS_KNOWLEDGE: {
    id: 'AGENTS_KNOWLEDGE',
    path: '/agents/knowledge',
    label: 'Knowledge',
  },
  AGENTS_ACTIONS: {
    id: 'AGENTS_ACTIONS',
    path: '/agents/actions',
    label: 'Actions & Skills',
  },
  SETTINGS: {
    id: 'SETTINGS',
    path: '/settings',
    label: 'Settings',
  },
  SETTINGS_AGENT: {
    id: 'SETTINGS_AGENT',
    path: '/settings/agent',
    label: 'Agent',
  },
  SETTINGS_KNOWLEDGE: {
    id: 'SETTINGS_KNOWLEDGE',
    path: '/settings/knowledge',
    label: 'Knowledge',
  },
  SETTINGS_INTEGRATIONS: {
    id: 'SETTINGS_INTEGRATIONS',
    path: '/settings/integrations',
    label: 'Integrations',
  },
  SETTINGS_BILLING: {
    id: 'SETTINGS_BILLING',
    path: '/settings/billing',
    label: 'Plan & Billing',
  },
  SETTINGS_COMPANY: {
    id: 'SETTINGS_COMPANY',
    path: '/settings/company',
    label: 'Company',
  },
  HELP: {
    id: 'HELP',
    path: 'https://help.example.com',
    label: 'Help Center',
  },
  DASHBOARD: {
    id: 'DASHBOARD',
    path: '/dashboard',
    label: 'Dashboard',
  },
  DASHBOARD_USERS: {
    id: 'DASHBOARD_USERS',
    path: '/dashboard/users',
    label: 'Users Management',
  },
  DASHBOARD_USER_PROFILE: {
    id: 'DASHBOARD_USER_PROFILE',
    path: '/dashboard/user-profile',
    label: 'User Profile',
  },
  DASHBOARD_USER_SESSIONS: {
    id: 'DASHBOARD_USER_SESSIONS',
    path: '/dashboard/user-sessions',
    label: 'User Sessions',
  },
  DASHBOARD_USER_SECURITY: {
    id: 'DASHBOARD_USER_SECURITY',
    path: '/dashboard/user-security',
    label: 'User Security',
  },
  DASHBOARD_ORGANIZATION_SWITCHER: {
    id: 'DASHBOARD_ORGANIZATION_SWITCHER',
    path: '/dashboard/organization-switcher',
    label: 'Organization Switcher',
  },
} as const

export type RouteId = keyof typeof ROUTE_REGISTRY
export type RouteSegment = (typeof ROUTE_REGISTRY)[RouteId]

export type BreadcrumbConfig = Array<{
  label: string
  href?: string
}>

export type RouteItem = {
  id: RouteId
  title: string
  path: string
  breadcrumbs: BreadcrumbConfig
}

export type RouteConfig = {
  id: RouteId
  title: string
  path: string
  icon: LucideIcon
  breadcrumbs: BreadcrumbConfig
  items?: Omit<RouteConfig, 'icon' | 'items'>[]
}

// Helper to build breadcrumbs based on route IDs
export function buildBreadcrumbs(routeId: RouteId): BreadcrumbConfig {
  // Special handling for dashboard routes
  if (routeId.startsWith('DASHBOARD_')) {
    return [
      {
        label: ROUTE_REGISTRY.DASHBOARD.label,
        href: ROUTE_REGISTRY.DASHBOARD.path,
      },
      {
        label: ROUTE_REGISTRY[routeId].label,
        href: ROUTE_REGISTRY[routeId].path,
      },
    ]
  }

  // Original logic for other routes
  const segments = routeId.split('_')
  return segments.map((_, index) => {
    const currentId = segments.slice(0, index + 1).join('_') as RouteId
    const route = ROUTE_REGISTRY[currentId]
    if (!route) throw new Error(`Route not found for ID: ${currentId}`)
    return {
      label: route.label,
      href: route.path,
    }
  })
}

// Main navigation routes with icons and subitems
export const ROUTES: Record<string, RouteConfig> = {
  ACTIVITY: {
    id: ROUTE_REGISTRY.ACTIVITY.id,
    title: ROUTE_REGISTRY.ACTIVITY.label,
    path: ROUTE_REGISTRY.ACTIVITY.path,
    icon: MessageSquareText,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIVITY.id),
    items: [
      {
        id: ROUTE_REGISTRY.ACTIVITY_OVERVIEW.id,
        title: ROUTE_REGISTRY.ACTIVITY_OVERVIEW.label,
        path: ROUTE_REGISTRY.ACTIVITY_OVERVIEW.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIVITY_OVERVIEW.id),
      },
      {
        id: ROUTE_REGISTRY.ACTIVITY_RECENT.id,
        title: ROUTE_REGISTRY.ACTIVITY_RECENT.label,
        path: ROUTE_REGISTRY.ACTIVITY_RECENT.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIVITY_RECENT.id),
      },
      {
        id: ROUTE_REGISTRY.ACTIVITY_ANALYTICS.id,
        title: ROUTE_REGISTRY.ACTIVITY_ANALYTICS.label,
        path: ROUTE_REGISTRY.ACTIVITY_ANALYTICS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIVITY_ANALYTICS.id),
      },
    ],
  },
  AGENTS: {
    id: ROUTE_REGISTRY.AGENTS.id,
    title: ROUTE_REGISTRY.AGENTS.label,
    path: ROUTE_REGISTRY.AGENTS.path,
    icon: Bot,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.AGENTS.id),
    items: [
      {
        id: ROUTE_REGISTRY.AGENTS_LIST.id,
        title: ROUTE_REGISTRY.AGENTS_LIST.label,
        path: ROUTE_REGISTRY.AGENTS_LIST.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.AGENTS_LIST.id),
      },
      {
        id: ROUTE_REGISTRY.AGENTS_KNOWLEDGE.id,
        title: ROUTE_REGISTRY.AGENTS_KNOWLEDGE.label,
        path: ROUTE_REGISTRY.AGENTS_KNOWLEDGE.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.AGENTS_KNOWLEDGE.id),
      },
      {
        id: ROUTE_REGISTRY.AGENTS_ACTIONS.id,
        title: ROUTE_REGISTRY.AGENTS_ACTIONS.label,
        path: ROUTE_REGISTRY.AGENTS_ACTIONS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.AGENTS_ACTIONS.id),
      },
    ],
  },
  SETTINGS: {
    id: ROUTE_REGISTRY.SETTINGS.id,
    title: ROUTE_REGISTRY.SETTINGS.label,
    path: ROUTE_REGISTRY.SETTINGS.path,
    icon: Settings,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS.id),
    items: [
      {
        id: ROUTE_REGISTRY.SETTINGS_AGENT.id,
        title: ROUTE_REGISTRY.SETTINGS_AGENT.label,
        path: ROUTE_REGISTRY.SETTINGS_AGENT.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_AGENT.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_COMPANY.id,
        title: ROUTE_REGISTRY.SETTINGS_COMPANY.label,
        path: ROUTE_REGISTRY.SETTINGS_COMPANY.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_COMPANY.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_KNOWLEDGE.id,
        title: ROUTE_REGISTRY.SETTINGS_KNOWLEDGE.label,
        path: ROUTE_REGISTRY.SETTINGS_KNOWLEDGE.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_KNOWLEDGE.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.id,
        title: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.label,
        path: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_BILLING.id,
        title: ROUTE_REGISTRY.SETTINGS_BILLING.label,
        path: ROUTE_REGISTRY.SETTINGS_BILLING.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_BILLING.id),
      },
    ],
  },
  HELP: {
    id: ROUTE_REGISTRY.HELP.id,
    title: ROUTE_REGISTRY.HELP.label,
    path: ROUTE_REGISTRY.HELP.path,
    icon: ExternalLink,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.HELP.id),
  },
}

// Helper to get all routes flattened (useful for route matching)
export const ALL_ROUTES = Object.values(ROUTES).reduce<RouteConfig[]>(
  (acc, route) => {
    if ('items' in route && route.items) {
      return [
        ...acc,
        route,
        ...(route.items.map(item => ({
          ...item,
          icon: route.icon,
        })) as RouteConfig[]),
      ]
    }
    return [...acc, route]
  },
  [],
)

// Helper to get breadcrumbs for a given path
export function getBreadcrumbs(path: string): BreadcrumbConfig {
  if (path.startsWith('/dashboard')) {
    const dashboardRoute = Object.values(ROUTE_REGISTRY).find(
      route => route.path === path,
    )
    if (dashboardRoute) {
      return buildBreadcrumbs(dashboardRoute.id)
    }
  }

  // Fall back to main routes
  const route = ALL_ROUTES.find(r => r.path === path)
  return route?.breadcrumbs || []
}

// Helper to get a route by its ID
export function getRouteById(id: RouteId): RouteSegment {
  return ROUTE_REGISTRY[id]
}

// Helper to get a route's full configuration by its ID
export function getRouteConfigById(id: RouteId): RouteConfig | undefined {
  const mainRoute = Object.values(ROUTES).find(route => route.id === id)
  if (mainRoute) return mainRoute

  // Search in subitems if not found in main routes
  for (const route of Object.values(ROUTES)) {
    if (route.items) {
      const subRoute = route.items.find(item => item.id === id)
      if (subRoute) {
        return {
          ...subRoute,
          icon: route.icon,
        }
      }
    }
  }
}
